/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_BROADCAST_FILTERS
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added tooltips to Clear Filters and Collapse/Expand buttons.
 *
 * File Size Check:
 * - Current: 551 lines (OVERSIZED)
 * - Adding: ~10 lines
 * - Projected: 561 lines
 * - Decision: Proceed (minimal change, split needed separately)
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_FILTERS_COMPONENT
 * Date:         2025-01-30
 * Modified By:  Cursor
 *
 * Description:
 * Dedicated filter component for broadcast search results with dynamic dropdowns.
 * All filter fields are dropdowns (except search query) populated from current filtered results.
 *
 * Related Screens / Modules:
 * - client/src/components/broadcast/BroadcastFilters.tsx
 * - client/src/components/Search.tsx
 */

import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSliders } from "@fortawesome/free-solid-svg-icons";
import { FlattenedBroadcastItem } from "./BroadcastItemCard";
import { ACTION_BUTTON_TOOLTIPS } from "../../constants/tooltips";
import "./BroadcastFilters.scss";

interface BroadcastFiltersProps {
  // Filter values
  searchQuery: string;
  skuFilter: string;
  typeFilter: string; // WTS/WTB filter
  manufacturerFilter: string;
  categoryFilter: string;
  qtyFilter: number;
  conditionFilter: string;
  countryFilter: string;
  companyFilter: string;

  // Change handlers
  onSearchChange: (value: string) => void;
  onSkuChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onManufacturerChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onQtyChange: (value: number) => void;
  onConditionChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onCompanyChange: (value: string) => void;
  onClearFilters: () => void;

  // Current broadcast items (for extracting unique values)
  broadcastItems: FlattenedBroadcastItem[];
  // All unfiltered items (for showing all options in the selected dropdown)
  allBroadcastItems: FlattenedBroadcastItem[];

  // Optional: Collapse state
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Utility functions to extract unique values from broadcast items
const extractUniqueSKUs = (items: FlattenedBroadcastItem[]): string[] => {
  const skus = new Set<string>();
  items.forEach((item) => {
    if (item.partNumber && item.partNumber.trim()) {
      skus.add(item.partNumber.trim());
    }
    if (item.model && item.model.trim()) {
      skus.add(item.model.trim());
    }
  });
  return Array.from(skus).sort();
};

const extractUniqueManufacturers = (items: FlattenedBroadcastItem[]): string[] => {
  const manufacturers = new Set<string>();
  items.forEach((item) => {
    if (item.manufacturer && item.manufacturer.trim()) {
      manufacturers.add(item.manufacturer.trim());
    }
  });
  return Array.from(manufacturers).sort();
};

const extractUniqueCategories = (items: FlattenedBroadcastItem[]): string[] => {
  const categories = new Set<string>();
  items.forEach((item) => {
    if (item.categories && Array.isArray(item.categories)) {
      item.categories.forEach((cat) => {
        if (cat && cat.trim()) {
          categories.add(cat.trim());
        }
      });
    }
  });
  return Array.from(categories).sort();
};

const extractUniqueQuantities = (items: FlattenedBroadcastItem[]): number[] => {
  const quantities = new Set<number>();
  items.forEach((item) => {
    if (item.quantity && item.quantity > 0) {
      quantities.add(item.quantity);
    }
  });
  return Array.from(quantities).sort((a, b) => a - b);
};

const extractUniqueConditions = (items: FlattenedBroadcastItem[]): string[] => {
  const conditions = new Set<string>();
  items.forEach((item) => {
    if (item.condition && item.condition.trim()) {
      conditions.add(item.condition.trim());
    }
  });
  return Array.from(conditions).sort();
};

const extractUniqueCountries = (items: FlattenedBroadcastItem[]): string[] => {
  const countries = new Set<string>();
  items.forEach((item) => {
    // Extract country from user who submitted the broadcast, not from item
    if (item.userCountry && item.userCountry.trim()) {
      countries.add(item.userCountry.trim());
    }
  });
  return Array.from(countries).sort();
};

const extractUniqueCompanies = (items: FlattenedBroadcastItem[]): string[] => {
  const companies = new Set<string>();
  items.forEach((item) => {
    if (item.companyName && item.companyName.trim()) {
      companies.add(item.companyName.trim());
    }
  });
  return Array.from(companies).sort();
};

const extractUniqueTypes = (items: FlattenedBroadcastItem[]): string[] => {
  const types = new Set<string>();
  items.forEach((item) => {
    if (item.broadcastGroup && item.broadcastGroup.type) {
      types.add(item.broadcastGroup.type);
    }
  });
  return Array.from(types).sort();
};

// Helper function to filter items excluding one specific field
// This allows each dropdown to show all available options when that field is changed
const filterExcludingField = (
  items: FlattenedBroadcastItem[],
  excludeField: string,
  filters: {
    searchQuery: string;
    skuFilter: string;
    typeFilter: string;
    manufacturerFilter: string;
    categoryFilter: string;
    qtyFilter: number;
    conditionFilter: string;
    countryFilter: string;
    companyFilter: string;
  },
): FlattenedBroadcastItem[] => {
  return items.filter((item) => {
    // Search query filter (keyword) - always apply unless this is the excluded field
    // Since searchQuery is not a dropdown field, we always apply it
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const keyword = filters.searchQuery.trim().toLowerCase();
      const matchesKeyword =
        (item.title && item.title.toLowerCase().includes(keyword)) ||
        (item.manufacturer && item.manufacturer.toLowerCase().includes(keyword)) ||
        (item.model && item.model.toLowerCase().includes(keyword)) ||
        (item.partNumber && item.partNumber.toLowerCase().includes(keyword)) ||
        (item.description && item.description.toLowerCase().includes(keyword));
      if (!matchesKeyword) return false;
    }

    // SKU filter (skip if this is the excluded field)
    if (excludeField !== "sku" && filters.skuFilter) {
      const matchesSKU =
        (item.partNumber && item.partNumber.trim() === filters.skuFilter) ||
        (item.model && item.model.trim() === filters.skuFilter);
      if (!matchesSKU) return false;
    }

    // Type filter (skip if this is the excluded field)
    if (excludeField !== "type" && filters.typeFilter) {
      if (item.broadcastGroup.type !== filters.typeFilter) return false;
    }

    // Manufacturer filter (skip if this is the excluded field)
    if (excludeField !== "manufacturer" && filters.manufacturerFilter) {
      if (!item.manufacturer || item.manufacturer.trim() !== filters.manufacturerFilter) {
        return false;
      }
    }

    // Category filter (skip if this is the excluded field)
    if (excludeField !== "category" && filters.categoryFilter) {
      if (!item.categories || !item.categories.includes(filters.categoryFilter)) {
        return false;
      }
    }

    // Quantity filter (skip if this is the excluded field)
    if (excludeField !== "qty" && filters.qtyFilter > 0) {
      if (item.quantity !== filters.qtyFilter) return false;
    }

    // Condition filter (skip if this is the excluded field)
    if (excludeField !== "condition" && filters.conditionFilter) {
      if (!item.condition || item.condition.trim() !== filters.conditionFilter) {
        return false;
      }
    }

    // Country filter (skip if this is the excluded field)
    if (excludeField !== "country" && filters.countryFilter) {
      if (!item.userCountry || item.userCountry.trim() !== filters.countryFilter) {
        return false;
      }
    }

    // Company filter (skip if this is the excluded field)
    if (excludeField !== "company" && filters.companyFilter) {
      if (!item.companyName || item.companyName.trim() !== filters.companyFilter) {
        return false;
      }
    }

    return true;
  });
};

export function BroadcastFilters({
  searchQuery,
  skuFilter,
  typeFilter,
  manufacturerFilter,
  categoryFilter,
  qtyFilter,
  conditionFilter,
  countryFilter,
  companyFilter,
  onSearchChange,
  onSkuChange,
  onTypeChange,
  onManufacturerChange,
  onCategoryChange,
  onQtyChange,
  onConditionChange,
  onCountryChange,
  onCompanyChange,
  onClearFilters,
  broadcastItems,
  allBroadcastItems,
  collapsed = false,
  onToggleCollapse,
}: BroadcastFiltersProps) {
  // Filter state object for helper function (memoized to prevent unnecessary re-renders)
  const filterState = useMemo(
    () => ({
      searchQuery,
      skuFilter,
      typeFilter,
      manufacturerFilter,
      categoryFilter,
      qtyFilter,
      conditionFilter,
      countryFilter,
      companyFilter,
    }),
    [
      searchQuery,
      skuFilter,
      typeFilter,
      manufacturerFilter,
      categoryFilter,
      qtyFilter,
      conditionFilter,
      countryFilter,
      companyFilter,
    ],
  );

  // For each dropdown, extract from items filtered by all OTHER filters
  // This allows the user to change their selection in that field
  // Note: searchQuery is always applied to filter items before extracting dropdown options
  const itemsForSKU = useMemo(
    () => filterExcludingField(allBroadcastItems, "sku", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForType = useMemo(
    () => filterExcludingField(allBroadcastItems, "type", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForManufacturer = useMemo(
    () => filterExcludingField(allBroadcastItems, "manufacturer", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForCategory = useMemo(
    () => filterExcludingField(allBroadcastItems, "category", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForQty = useMemo(
    () => filterExcludingField(allBroadcastItems, "qty", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForCondition = useMemo(
    () => filterExcludingField(allBroadcastItems, "condition", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForCountry = useMemo(
    () => filterExcludingField(allBroadcastItems, "country", filterState),
    [allBroadcastItems, filterState],
  );
  const itemsForCompany = useMemo(
    () => filterExcludingField(allBroadcastItems, "company", filterState),
    [allBroadcastItems, filterState],
  );

  // Extract unique values from appropriately filtered items (memoized for performance)
  const uniqueSKUs = useMemo(() => extractUniqueSKUs(itemsForSKU), [itemsForSKU]);
  const uniqueTypes = useMemo(() => extractUniqueTypes(itemsForType), [itemsForType]);
  const uniqueManufacturers = useMemo(
    () => extractUniqueManufacturers(itemsForManufacturer),
    [itemsForManufacturer],
  );
  const uniqueCategories = useMemo(
    () => extractUniqueCategories(itemsForCategory),
    [itemsForCategory],
  );
  const uniqueQuantities = useMemo(() => extractUniqueQuantities(itemsForQty), [itemsForQty]);
  const uniqueConditions = useMemo(
    () => extractUniqueConditions(itemsForCondition),
    [itemsForCondition],
  );
  const uniqueCountries = useMemo(() => extractUniqueCountries(itemsForCountry), [itemsForCountry]);
  const uniqueCompanies = useMemo(() => extractUniqueCompanies(itemsForCompany), [itemsForCompany]);

  return (
    <div className="broadcast-filters">
      <div
        className={`${
          collapsed
            ? "d-flex justify-content-center"
            : "d-flex justify-content-between align-items-center"
        } mb-3`}
      >
        {!collapsed && <h5 className="m-0 filter-title">Filters</h5>}
        {onToggleCollapse && (
          <button
            className={`btn-figma-1 btn-sm filter-collapse-btn ${collapsed ? "filter-collapse-btn--collapsed" : ""}`}
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand filters" : "Collapse filters"}
            title={collapsed ? ACTION_BUTTON_TOOLTIPS.EXPAND_FILTERS : ACTION_BUTTON_TOOLTIPS.COLLAPSE_FILTERS}
          >
            {collapsed ? <FontAwesomeIcon icon={faSliders} /> : <FontAwesomeIcon icon={faTimes} />}
          </button>
        )}
      </div>

      {/* Filter content - only show when not collapsed */}
      <div className={`filter-content ${collapsed ? "d-none" : ""}`}>
        {!collapsed && (
          <>
            {/* Search Input - SKU/Model/Part Number, Keyword, Manufacturer */}
            <Form.Group className="mb-3 filter-first-group">
              <Form.Label htmlFor="search" className="filter-label">
                keyword
              </Form.Label>
              <Form.Control
                type="text"
                id="search"
                className="filter-input"
                placeholder="Input"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Form.Group>

            {/* SKU/Model/Part Number Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="sku" className="filter-label">
                SKU/Model/Part Number
              </Form.Label>
              <Form.Select
                id="sku"
                className="filter-input"
                value={skuFilter}
                onChange={(e) => onSkuChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueSKUs.map((sku) => (
                  <option key={sku} value={sku}>
                    {sku}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* WTS/WTB Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="type" className="filter-label">
                Type
              </Form.Label>
              <Form.Select
                id="type"
                className="filter-input"
                value={typeFilter}
                onChange={(e) => onTypeChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Category Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="category" className="filter-label">
                Category
              </Form.Label>
              <Form.Select
                id="category"
                className="filter-input"
                value={categoryFilter}
                onChange={(e) => onCategoryChange(e.target.value)}
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* QTY Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="qty" className="filter-label">
                QTY - quantity
              </Form.Label>
              <Form.Select
                id="qty"
                className="filter-input"
                value={qtyFilter || ""}
                onChange={(e) => onQtyChange(parseInt(e.target.value) || 0)}
              >
                <option value="">All Quantities</option>
                {uniqueQuantities.map((qty) => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Condition Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="condition" className="filter-label">
                Condition
              </Form.Label>
              <Form.Select
                id="condition"
                className="filter-input"
                value={conditionFilter}
                onChange={(e) => onConditionChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueConditions.map((cond) => (
                  <option key={cond} value={cond}>
                    {cond}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Country Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="country" className="filter-label">
                Country
              </Form.Label>
              <Form.Select
                id="country"
                className="filter-input"
                value={countryFilter}
                onChange={(e) => onCountryChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueCountries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Manufacturer Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="manufacturer" className="filter-label">
                Manufacturer
              </Form.Label>
              <Form.Select
                id="manufacturer"
                className="filter-input"
                value={manufacturerFilter}
                onChange={(e) => onManufacturerChange(e.target.value)}
              >
                <option value="">All Manufacturers</option>
                {uniqueManufacturers.map((mfr) => (
                  <option key={mfr} value={mfr}>
                    {mfr}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Company Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="company" className="filter-label">
                Company
              </Form.Label>
              <Form.Select
                id="company"
                className="filter-input"
                value={companyFilter}
                onChange={(e) => onCompanyChange(e.target.value)}
              >
                <option value="">Company</option>
                {uniqueCompanies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Clear filters */}
            <button
              className="btn-cancel-figma w-100"
              onClick={onClearFilters}
              title={ACTION_BUTTON_TOOLTIPS.CLEAR_FILTERS}
            >
              Clear All Filters
            </button>
          </>
        )}
      </div>
    </div>
  );
}
